// umlConst and ShapeGeoBuilder are re-exported in the @jxUml barrel
// (UmlWorkspace/index.js) together with AnchorCalculator itself — import by
// relative path here to avoid a self-referential cycle through that barrel.
import ShapeGeoBuilder from '../../Types/ShapeGeoBuilder.js';
import umlConst from '../../Types/UmlConsts.js';

/**
 * Shape-anchor geometry: where an anchor point lands on a shape's border.
 * Stateless, static methods only — no DOM measurement, so clone-safe.
 */
class AnchorCalculator {
    static clamp(value, min, max) {
        if (min > max) return (min + max) / 2; // shape smaller than 2*ANCHOR_EDGE_MARGIN — just center
        return Math.min(max, Math.max(min, value));
    }

    /**
     * Computes the point where a line from the shape's center toward (towardX, towardY)
     * crosses the shape's edge, using the collapsed header height instead of the full
     * shape height when collapsed.
     * Picks the edge (top/bottom vs left/right) by the dominant axis (whichever offset
     * is larger) rather than the geometric line-intersection, so the connector stays
     * flush with the correct border even when the two shapes aren't perfectly aligned
     * on that axis.
     * @param {object} shape - shape with x/y/w/h and optional collapsed flag
     * @param {number} towardX - target x coordinate
     * @param {number} towardY - target y coordinate
     * @returns {{x:number, y:number}} edge point
     */
    static edgeToward(shape, towardX, towardY) {
        const effectiveH = ShapeGeoBuilder.effectiveHeight(shape);
        const cx = shape.x + shape.w / 2;
        const cy = shape.y + effectiveH / 2;
        const dx = towardX - cx;
        const dy = towardY - cy;
        if (dx === 0 && dy === 0) {
            return { x: cx, y: cy };
        }
        const halfW = shape.w / 2;
        const halfH = effectiveH / 2;

        const isVerticalDominant = Math.abs(dy) >= Math.abs(dx);
        if (isVerticalDominant) {
            const y = dy > 0 ? cy + halfH : cy - halfH;
            const x = AnchorCalculator.clamp(towardX, cx - halfW + umlConst.ANCHOR_EDGE_MARGIN, cx + halfW - umlConst.ANCHOR_EDGE_MARGIN);
            return { x, y };
        }

        const x = dx > 0 ? cx + halfW : cx - halfW;
        const y = AnchorCalculator.clamp(towardY, cy - halfH + umlConst.ANCHOR_EDGE_MARGIN, cy + halfH - umlConst.ANCHOR_EDGE_MARGIN);
        return { x, y };
    }

    /**
     * Clamps a manual anchor offset to the shape's current bounds.
     * A fixed pixel offset from shape.x/y, not a proportional center projection.
     *
     * @param {object} shape - shape with x/y/w/h and optional collapsed flag
     * @param {{x:number, y:number}} anchorOffset - offset relative to shape origin
     * @returns {{x:number, y:number}} absolute clamped anchor point
     */
    static toShape(shape, anchorOffset) {
        const effectiveH = ShapeGeoBuilder.effectiveHeight(shape);
        const clampedX = Math.min(Math.max(anchorOffset.x, 0), shape.w);
        const clampedY = Math.min(Math.max(anchorOffset.y, 0), effectiveH);

        return { x: shape.x + clampedX, y: shape.y + clampedY };
    }
}

export default AnchorCalculator;
